/**
 * NotificationsPage — shows recent collection additions by pod members.
 *
 * Auto-marks all notifications as read when the page is visited.
 * Groups entries by relative date (Today, Yesterday, This week, Earlier).
 */

import React, { useEffect } from 'react';

// ── Date helpers ──────────────────────────────────────────────────────────────

function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function relativeGroup(isoString) {
    const now = new Date();
    const date = new Date(isoString);
    const todayStart = startOfDay(now);
    const diffDays = Math.floor((todayStart - startOfDay(date)) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return 'This week';
    return 'Earlier';
}

const GROUP_ORDER = ['Today', 'Yesterday', 'This week', 'Earlier'];

function formatTime(isoString) {
    return new Date(isoString).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: new Date(isoString).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
}

// ── Cover thumbnail ───────────────────────────────────────────────────────────

function CoverThumb({ src, alt }) {
    if (!src) {
        return (
            <div className="w-10 h-14 rounded bg-bg-tertiary flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 21l6.75-6.75 1.5 1.5M21 12V6.75A2.25 2.25 0 0018.75 4.5H5.25A2.25 2.25 0 003 6.75v10.5A2.25 2.25 0 005.25 19.5H9" />
                </svg>
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={alt}
            className="w-10 h-14 object-cover rounded flex-shrink-0"
            loading="lazy"
        />
    );
}

// ── Single notification row ───────────────────────────────────────────────────

function PodJoinThumb() {
    return (
        <div className="w-10 h-14 rounded bg-accent-primary/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-accent-primary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
        </div>
    );
}

function NotificationRow({ notification, isUnread }) {
    const isPodJoin = notification.type === 'pod_join';
    return (
        <div className={[
            'flex items-center gap-3 px-4 py-3 transition-colors duration-100',
            isUnread ? 'bg-accent-primary/5' : '',
        ].join(' ')}>
            {isPodJoin ? (
                <PodJoinThumb />
            ) : (
                <CoverThumb src={notification.coverURL} alt={notification.itemTitle} />
            )}
            <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary leading-snug">
                    <span className="mr-1">{notification.userEmoji}</span>
                    <span className="font-medium">{notification.userName}</span>
                    {isPodJoin ? (
                        <> joined <span className="font-medium">{notification.podName}</span></>
                    ) : (
                        <> added <span className="font-medium">{notification.itemTitle}</span></>
                    )}
                </p>
                <p className="text-xs text-text-tertiary mt-0.5">{formatTime(notification.created)}</p>
            </div>
            {isUnread && (
                <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
            )}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {{ notifications: Array, unreadCount: number, markAllRead: () => void, loading: boolean }} props
 */
export default function NotificationsPage({ notifications, unreadCount, markAllRead, loading }) {
    // Mark all read as soon as the page mounts
    useEffect(() => {
        if (unreadCount > 0) markAllRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally run only on mount

    // Group notifications
    const groups = {};
    for (const n of notifications) {
        const g = relativeGroup(n.created);
        if (!groups[g]) groups[g] = [];
        groups[g].push(n);
    }

    const lastReadAt = localStorage.getItem(
        `notifications-last-read-${notifications[0]?.appType ?? ''}`,
    ) || new Date(0).toISOString();

    return (
        <div className="max-w-lg mx-auto py-6 px-4">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-semibold text-text-primary">Notifications</h1>
            </div>

            {loading && notifications.length === 0 && (
                <div className="space-y-3">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="flex gap-3 items-center px-4 py-3 animate-pulse">
                            <div className="w-10 h-14 rounded bg-bg-tertiary flex-shrink-0" />
                            <div className="flex-1 space-y-2">
                                <div className="h-3 bg-bg-tertiary rounded w-3/4" />
                                <div className="h-2 bg-bg-tertiary rounded w-1/4" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && notifications.length === 0 && (
                <div className="text-center py-16">
                    <p className="text-4xl mb-3">🔔</p>
                    <p className="text-text-secondary text-sm">No notifications yet</p>
                    <p className="text-text-tertiary text-xs mt-1">
                        You'll see activity here when your pod members add titles or join your pod
                    </p>
                </div>
            )}

            {GROUP_ORDER.filter((g) => groups[g]).map((group) => (
                <div key={group} className="mb-4">
                    <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider px-4 pb-1 mb-1">
                        {group}
                    </p>
                    <div className="rounded-xl bg-surface border border-border-subtle overflow-hidden divide-y divide-border-subtle">
                        {groups[group].map((n) => (
                            <NotificationRow
                                key={n.id}
                                notification={n}
                                isUnread={n.created > lastReadAt}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
